<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl" v-if="!isLoading">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Edit Notification
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.user_id"
                :items="userItems"
                item-text="text"
                item-value="value"
                outlined
                label="Target User"
                prepend-inner-icon="mdi-account"
                :error-messages="errors.user_id"
                clearable
                hint="Leave empty to send to all users"
                persistent-hint
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.type_id"
                :items="notificationTypes"
                item-text="text"
                item-value="value"
                outlined
                label="Notification Type *"
                prepend-inner-icon="mdi-tag"
                :error-messages="errors.type_id"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.message"
                outlined
                label="Message *"
                prepend-inner-icon="mdi-message-text"
                :error-messages="errors.message"
                rows="4"
                counter
                hint="Enter the notification message"
                persistent-hint
              ></v-textarea>
            </v-col>
            <v-col sm="6">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.status"
                inset
                :label="`Read Status: ${form.status ? 'READ' : 'UNREAD'}`"
                hint="Current read status"
                persistent-hint
              ></v-switch>
            </v-col>
            <v-col sm="6">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Notification Details Display -->
          <v-row v-if="form.id">
            <v-col cols="12">
              <v-alert
                :type="getNotificationAlertType()"
                :icon="getNotificationIcon()"
                outlined
                prominent
              >
                <div class="title">{{ getNotificationStatus() }}</div>
                <div>{{ getNotificationInfo() }}</div>
              </v-alert>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"></v-divider>
          <v-btn
            :loading="isSaving"
            :disabled="isSaving"
            rounded
            color="primary"
            dark
            @click="save"
          >
            Update Notification
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Loading State -->
      <v-card elevation="6" rounded="xxl" v-else>
        <v-card-text class="text-center" style="height: 400px">
          <health-loader message="Loading notification data..." />
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "NotificationEditPage",
  data() {
    return {
      form: {
        id: null,
        user_id: null,
        type_id: 1,
        message: "",
        status: false,
        is_active: true,
      },
      errors: {},
      isLoading: true,
      isSaving: false,
      userItems: [],
      notificationTypes: [
        { text: "System", value: 1 },
        { text: "User", value: 2 },
        { text: "Order", value: 3 },
        { text: "Payment", value: 4 },
        { text: "Alert", value: 5 },
      ],
    };
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("/masters?context=notifications");
        if (response.data && response.data.users) {
          const users = response.data.users.data || response.data.users;
          this.userItems = users.map((user) => ({
            text: user.user_name || `${user.first_name} ${user.last_name}`,
            value: user.id,
          }));

          // Add "All Users" option
          this.userItems.unshift({
            text: "All Users",
            value: null,
          });
        }
      } catch (error) {
        console.error("Error loading masters:", error);
      }
    },

    async getData() {
      try {
        this.isLoading = true;
        const encryptedId = this.$route.params.id;
        const response = await axios.get(`/notifications/${encryptedId}`);

        if (response.data.success) {
          this.form = { ...response.data.data };
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to load notification",
          });
          this.$router.push("/notifications");
        }
      } catch (error) {
        console.error("Error loading notification:", error);

        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Notification not found",
          });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else if (error.response && error.response.status === 403) {
          this.$store.dispatch("errors/setError", {
            message: "You don't have permission to view this notification",
          });
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading notification data",
          });
        }
        this.$router.push("/notifications");
      } finally {
        this.isLoading = false;
      }
    },

    getNotificationStatus() {
      const typeName = this.getTypeName(this.form.type_id);
      const statusText = this.form.status ? "Read" : "Unread";
      return `${typeName} Notification - ${statusText}`;
    },

    getNotificationInfo() {
      const user = this.form.user ? this.form.user.user_name : "All Users";
      const createdAt = this.formatDate(this.form.created_at);
      return `Target: ${user} | Created: ${createdAt}`;
    },

    getNotificationAlertType() {
      if (!this.form.is_active) return "error";
      return this.form.status ? "success" : "info";
    },

    getNotificationIcon() {
      const icons = {
        1: "mdi-cog", // System
        2: "mdi-account", // User
        3: "mdi-cart", // Order
        4: "mdi-credit-card", // Payment
        5: "mdi-alert", // Alert
      };
      return icons[this.form.type_id] || "mdi-bell";
    },

    getTypeName(typeId) {
      const type = this.notificationTypes.find((t) => t.value === typeId);
      return type ? type.text : "Unknown";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleString();
    },

    async save() {
      try {
        this.isSaving = true;
        this.errors = {};

        const encryptedId = this.$route.params.id;
        const response = await axios.patch(
          `/notifications/${encryptedId}`,
          this.form
        );

        if (response.data.success) {
          this.$store.dispatch("errors/setSuccess", {
            message: "Notification updated successfully",
          });
          this.$router.push("/notifications");
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to update notification",
          });
        }
      } catch (error) {
        console.error("Error updating notification:", error);

        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else if (error.response && error.response.status === 403) {
          this.$store.dispatch("errors/setError", {
            message: "You don't have permission to update this notification",
          });
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error updating notification",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>